/**
 * lib/auth/middleware.ts
 * ======================
 * TanStack Start server middleware that validates the Cognito session (httpOnly
 * JWT cookies). Drop-in replacement for the previous better-auth middleware —
 * the injected context keeps the same shape:
 *   { userId, session, user[, roles] }
 * where `userId` is the user's custom:legacy_id (legacy UUID), so every
 * downstream query / role check is unchanged.
 *
 * Usage in a server function:
 *   import { requireAuth } from '@/lib/auth/middleware';
 *
 *   export const myServerFn = createServerFn()
 *     .middleware([requireAuth])
 *     .handler(async ({ context }) => {
 *       const { userId } = context;
 *       // ...
 *     });
 */

import { createMiddleware } from '@tanstack/react-start';
import { resolveSession, type AppSession } from './verify.server';
import { getUserRoles } from './access.server';

/** Shape the verified session into the legacy { user, session } context. */
function toContext(s: AppSession) {
  return {
    userId: s.userId,
    user: {
      id: s.userId,
      email: s.email,
      name: s.name,
      image: s.image,
      sub: s.sub,
    },
    session: {
      userId: s.userId,
      token: s.accessToken,
      expiresAt: s.expiresAt,
    },
  };
}

/**
 * requireAuth
 * -----------
 * Verifies the Cognito session cookies (refreshing if the access/id token
 * expired). Injects { userId, session, user } and throws 401 if unauthenticated.
 */
export const requireAuth = createMiddleware({ type: 'function' }).server(
  async ({ next }) => {
    const session = await resolveSession();
    if (!session) {
      throw new Response('Unauthorized: No valid session', { status: 401 });
    }
    return next({ context: toContext(session) });
  },
);

/**
 * requireAdmin
 * ------------
 * Like requireAuth, but also requires the caller to hold the `admin` role
 * (from public.user_roles, keyed on the legacy UUID). Injects `roles`.
 *
 * Defined here (not in access.server.ts) so `.middleware([requireAdmin])` on
 * client-reachable server fns does not pull the server-only access module into
 * the client bundle — getUserRoles is referenced only inside the stripped
 * `.server()` body.
 */
export const requireAdmin = createMiddleware({ type: 'function' }).server(
  async ({ next }) => {
    const session = await resolveSession();
    if (!session) {
      throw new Response('Unauthorized: No valid session', { status: 401 });
    }
    const roles = await getUserRoles(session.userId);
    if (!roles.includes('admin')) {
      throw new Response('Forbidden: admin role required', { status: 403 });
    }
    return next({ context: { ...toContext(session), roles } });
  },
);

/**
 * optionalAuth
 * ------------
 * Same as requireAuth but does not throw if no session exists. Injects
 * { userId, session, user } with nulls for anonymous callers.
 */
export const optionalAuth = createMiddleware({ type: 'function' }).server(
  async ({ next }) => {
    let resolved: AppSession | null = null;
    try {
      resolved = await resolveSession();
    } catch {
      resolved = null;
    }

    // Build ONE object literal with nullable properties (not a union of two
    // object types) so TanStack infers a single, well-defined context shape.
    let userId: string | null = null;
    let user: ReturnType<typeof toContext>['user'] | null = null;
    let session: ReturnType<typeof toContext>['session'] | null = null;
    if (resolved) {
      const ctx = toContext(resolved);
      userId = ctx.userId;
      user = ctx.user;
      session = ctx.session;
    }
    return next({ context: { userId, session, user } });
  },
);
